// import components
import { Alert, Button } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import ExcelImportFindings from './components/ExcelImportFindings';
import {
  StyledFileNameText,
  StyledFindingsBox,
  StyledHiddenInput,
  StyledImportRow
} from './ExcelImport.styles';

// import hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// import types
import type { AxiosError } from 'axios';
import type { ChangeEvent } from 'react';
import type { ExcelFinding, ExcelImportProps } from './ExcelImport.types';

// import utils
import { readExcelFile } from './ExcelImport.utils';
import showError from 'components/shared/showError';

const ExcelImport = ({
  columns,
  maxRows,
  onRows,
  disabled
}: ExcelImportProps) => {
  'use memo';
  const { t } = useTranslation();
  const [fileName, setFileName] = useState<string | null>(null);
  const [findings, setFindings] = useState<ExcelFinding[]>([]);
  const [tooManyRows, setTooManyRows] = useState<{
    found: number;
    max: number;
  } | null>(null);
  const [isParsing, setIsParsing] = useState(false);

  const onChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setFileName(file.name);
    setFindings([]);
    setTooManyRows(null);
    onRows(null);
    setIsParsing(true);

    // The React Compiler (this version) can't lower a try/catch whose body contains a
    // conditional, so the fallible read is isolated behind .catch() instead.
    const result = await readExcelFile(file, columns, maxRows, {
      expectedNumber: t(
        'adminPortal.servicedesk.excelImport.findings.expectedNumber'
      ),
      expectedRequired: t(
        'adminPortal.servicedesk.excelImport.findings.expectedRequired'
      ),
      expectedDate: t(
        'adminPortal.servicedesk.excelImport.findings.expectedDate'
      ),
      unsupportedColumnCountExpected: t(
        'adminPortal.servicedesk.excelImport.findings.unsupportedColumnCountExpected'
      )
    }).catch((error: AxiosError) => {
      showError(error);
      return null;
    });

    setIsParsing(false);
    if (!result) return;

    setFindings(result.findings);
    setTooManyRows(result.tooManyRows);

    if (result.findings.length === 0 && !result.tooManyRows) {
      onRows(result.rows);
    }
  };

  return (
    <div>
      <StyledImportRow>
        <Button
          variant="outlined"
          component="label"
          disabled={disabled || isParsing}
          startIcon={<Icon type="file-xls" />}
        >
          {t('adminPortal.servicedesk.excelImport.chooseFile')}
          <StyledHiddenInput type="file" accept=".xlsx" onChange={onChange} />
        </Button>
        {fileName && <StyledFileNameText>{fileName}</StyledFileNameText>}
      </StyledImportRow>

      {tooManyRows && (
        <Alert severity="error">
          {t('adminPortal.servicedesk.excelImport.tooManyRows', tooManyRows)}
        </Alert>
      )}

      {findings.length > 0 && (
        <StyledFindingsBox>
          <ExcelImportFindings findings={findings} />
        </StyledFindingsBox>
      )}
    </div>
  );
};

export default ExcelImport;
